/**
 * Recognises a keyboard-emulating ("wedge") barcode scanner by the shape of its keystrokes.
 *
 * Scanners type a whole code in a burst of a few milliseconds per key and end it with Enter or Tab;
 * people don't. A burst is accepted when its mean key interval is under MaxMeanIntervalMS and no
 * single gap exceeds MaxGapMS. A scanner programmed with a Prefix character is recognised by the
 * prefix alone, with no timing involved.
 *
 * Pure logic: feed it key names and timestamps; the DOM binding lives in Wedge-Scan-Source.
 */

/**
 * @typedef {Object} WedgeOptions
 * @property {number} [MaxMeanIntervalMS] - Default 35.
 * @property {number} [MaxGapMS] - Largest single gap inside a burst. Default 80.
 * @property {number} [MinLength] - Default 3.
 * @property {Array<string>} [Terminators] - Key names that end a scan. Default Enter and Tab.
 * @property {string|null} [Prefix] - Optional character the scanner sends first.
 */

/**
 * @typedef {Object} WedgeKeyResult
 * @property {'none'|'buffering'|'complete'} State
 * @property {boolean} Consume - True when the key belongs to a recognised scan and should not reach the page.
 * @property {string} [Value] - The scanned text, when State is complete.
 * @property {number} [MeanIntervalMS]
 */

class WedgeBurstDetector
{
	/**
	 * @param {WedgeOptions} [pOptions]
	 */
	constructor(pOptions)
	{
		const tmpOptions = pOptions || {};
		this.maxMeanInterval = tmpOptions.MaxMeanIntervalMS || 35;
		this.maxGap = tmpOptions.MaxGapMS || 80;
		this.minLength = tmpOptions.MinLength || 3;
		this.terminators = tmpOptions.Terminators || [ 'Enter', 'Tab' ];
		this.prefix = tmpOptions.Prefix || null;
		this.reset();
	}

	reset()
	{
		/** @type {Array<string>} */
		this.buffer = [];
		/** @type {Array<number>} */
		this.times = [];
		this.inPrefixScan = false;
	}

	/**
	 * @param {string} pKey - KeyboardEvent.key
	 * @param {number} pTime - Milliseconds (event.timeStamp or performance.now()).
	 * @return {WedgeKeyResult}
	 */
	handleKey(pKey, pTime)
	{
		if (this.prefix && pKey === this.prefix)
		{
			this.reset();
			this.inPrefixScan = true;
			return { State: 'buffering', Consume: true };
		}

		if (this.terminators.includes(pKey))
		{
			return this._complete(pTime);
		}

		if (typeof pKey !== 'string' || pKey.length !== 1)
		{
			if (!this.inPrefixScan && pKey !== 'Shift')
			{
				this.reset();
			}
			return { State: this.inPrefixScan ? 'buffering' : 'none', Consume: this.inPrefixScan };
		}

		if (!this.inPrefixScan && this.times.length > 0 && (pTime - this.times[this.times.length - 1]) > this.maxGap)
		{
			this.reset();
		}
		this.buffer.push(pKey);
		this.times.push(pTime);
		return { State: 'buffering', Consume: this.inPrefixScan };
	}

	/**
	 * @param {number} pTime
	 * @return {WedgeKeyResult}
	 */
	_complete(pTime)
	{
		const tmpValue = this.buffer.join('');
		const tmpWasPrefixScan = this.inPrefixScan;
		const tmpTimes = this.times.slice();
		this.reset();

		if (tmpWasPrefixScan)
		{
			return tmpValue.length > 0 ? { State: 'complete', Consume: true, Value: tmpValue } : { State: 'none', Consume: true };
		}
		if (tmpValue.length < this.minLength)
		{
			return { State: 'none', Consume: false };
		}
		const tmpIntervals = [];
		for (let i = 1; i < tmpTimes.length; i++)
		{
			tmpIntervals.push(tmpTimes[i] - tmpTimes[i - 1]);
		}
		tmpIntervals.push(pTime - tmpTimes[tmpTimes.length - 1]);
		const tmpMean = tmpIntervals.reduce((pSum, pValue) => pSum + pValue, 0) / tmpIntervals.length;
		const tmpLargest = Math.max(...tmpIntervals);
		if (tmpMean <= this.maxMeanInterval && tmpLargest <= this.maxGap)
		{
			return { State: 'complete', Consume: true, Value: tmpValue, MeanIntervalMS: tmpMean };
		}
		return { State: 'none', Consume: false, MeanIntervalMS: tmpMean };
	}
}

module.exports = WedgeBurstDetector;
